import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ChunkAnchor } from '@shared/schemas/rag-anchors';
import type { DocumentViewerProps } from './DocumentViewer';

export type ViewerKind = 'pdf' | 'docx_html' | 'xlsx' | 'pptx' | 'unsupported';

export const PDF_MIME_TYPE = 'application/pdf';
export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const PPTX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

type ViewerEntry = {
  anchorKinds: readonly string[];
  kind: ViewerKind;
  load: () => Promise<{ default: ComponentType<DocumentViewerProps> }>;
  mediaTypes: readonly string[];
};

type ViewerComponent = LazyExoticComponent<ComponentType<DocumentViewerProps>>;

export const DOCUMENT_VIEWER_REGISTRY: readonly ViewerEntry[] = [
  {
    anchorKinds: ['pdf'],
    kind: 'pdf',
    load: () => import('./PdfViewer.entry'),
    mediaTypes: [PDF_MIME_TYPE],
  },
  {
    anchorKinds: ['docx_html'],
    kind: 'docx_html',
    load: () => import('./DocxViewer.entry'),
    mediaTypes: [DOCX_MIME_TYPE],
  },
  {
    anchorKinds: ['xlsx'],
    kind: 'xlsx',
    load: () => import('./XlsxViewer'),
    mediaTypes: [XLSX_MIME_TYPE],
  },
  {
    anchorKinds: ['pptx'],
    kind: 'pptx',
    load: () => import('./PptxViewer'),
    mediaTypes: [PPTX_MIME_TYPE],
  },
];

export function resolveViewerKind(anchor: ChunkAnchor | null | undefined, mediaType: string): ViewerKind {
  const anchorKind = anchor?.kind;
  const anchorEntry = anchorKind
    ? DOCUMENT_VIEWER_REGISTRY.find((candidate) => candidate.anchorKinds.includes(anchorKind))
    : null;
  if (anchorEntry) {
    return anchorEntry.kind;
  }

  const entry = DOCUMENT_VIEWER_REGISTRY.find((candidate) => candidate.mediaTypes.includes(mediaType));

  return entry?.kind ?? 'unsupported';
}

export function getViewerLoader(kind: ViewerKind): ViewerEntry['load'] {
  if (kind === 'unsupported') {
    return () => import('./UnsupportedViewer.entry');
  }

  const entry = DOCUMENT_VIEWER_REGISTRY.find((candidate) => candidate.kind === kind);
  return entry?.load ?? (() => import('./UnsupportedViewer.entry'));
}

const VIEWER_COMPONENTS: Record<ViewerKind, ViewerComponent> = {
  docx_html: lazy(getViewerLoader('docx_html')),
  pdf: lazy(getViewerLoader('pdf')),
  pptx: lazy(getViewerLoader('pptx')),
  unsupported: lazy(getViewerLoader('unsupported')),
  xlsx: lazy(getViewerLoader('xlsx')),
};

export function getViewerComponent(kind: ViewerKind): ViewerComponent {
  return VIEWER_COMPONENTS[kind] ?? VIEWER_COMPONENTS.unsupported;
}
